import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  can,
  formatCommercialJobNumber,
  type JobStageColor,
  type JobStageOutcome,
  type ListInput,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { useSession } from '../session';
import { Button } from '../ui/Button';
import { Notice, StatusBadge, type Tone } from '../ui/Badge';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { field } from '../create/Field';
import { money } from '../money';
import { IconJobs } from '../icons';
import { useOnline } from '../ui/useConnection';

export type CommercialJobRow = {
  id: string;
  jobNumber: number;
  title: string;
  customerName: string | null;
  ownerName: string | null;
  serviceTypeLabel: string | null;
  pipelineId: string;
  stageId: string;
  stageLabel: string;
  stageColor: string;
  version: number;
  openQuotedCents: number | null | undefined;
  acceptedPendingCents: number | null | undefined;
  confirmedCents: number | null | undefined;
  canceledCents?: number | null;
  workOrderCount: number;
};

export type JobPipelineStage = {
  id: string;
  label: string;
  color: string;
  outcome: string;
  active: boolean;
  version: number;
};
export type JobPipeline = {
  id: string;
  label: string;
  active: boolean;
  version: number;
  stages: JobPipelineStage[];
};

const STAGE_TONES: Record<JobStageColor, Tone> = {
  slate: 'neutral',
  blue: 'info',
  amber: 'warn',
  green: 'success',
  red: 'danger',
  purple: 'brand',
};
export function stageTone(color: string | null | undefined): Tone {
  return STAGE_TONES[color as JobStageColor] ?? 'neutral';
}

/** Three active amounts and separate canceled history, all calculated by the server. */
export function JobValueBuckets({
  values,
}: {
  values: Partial<
    Pick<
      CommercialJobRow,
      'openQuotedCents' | 'acceptedPendingCents' | 'confirmedCents' | 'canceledCents'
    >
  >;
}) {
  return (
    <dl className="space-y-1 text-xs">
      <div className="flex justify-between gap-3">
        <dt className="text-muted-foreground">Open quoted</dt>
        <dd className="font-medium tabular-nums">{money(values.openQuotedCents)}</dd>
      </div>
      <div className="flex justify-between gap-3">
        <dt className="text-muted-foreground">Accepted · awaiting office</dt>
        <dd className="font-medium tabular-nums">{money(values.acceptedPendingCents)}</dd>
      </div>
      <div className="flex justify-between gap-3">
        <dt className="text-muted-foreground">Confirmed work orders</dt>
        <dd className="font-medium tabular-nums">{money(values.confirmedCents)}</dd>
      </div>
      <div className="flex justify-between gap-3 border-t border-border pt-1">
        <dt className="text-muted-foreground">Canceled work orders · historical</dt>
        <dd className="font-medium tabular-nums">{money(values.canceledCents)}</dd>
      </div>
    </dl>
  );
}

export const PIPELINE_PAGE_SIZE = 25;

/** Columns have independent server pages. A 200-row board cap must never hide older Jobs. */
export function JobPipelineBoard({
  pipeline,
  search,
  filters,
  ownerUserId,
  serviceTypeId,
  outcome,
}: {
  pipeline: JobPipeline;
  search?: string;
  filters?: ListInput['filters'];
  ownerUserId?: string;
  serviceTypeId?: string;
  outcome?: JobStageOutcome;
}) {
  const stages = pipeline.stages.filter((stage) => stage.active);
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Stage changes update the deal only. Confirm accepted quotes from the Job to create
        work orders.
      </p>
      <div
        className="h-scroll pb-3"
        tabIndex={0}
        role="region"
        aria-label={`${pipeline.label} pipeline`}
      >
        <div className="flex min-w-max items-start gap-3">
          {stages.map((stage) => (
            <PipelineColumn
              key={JSON.stringify([
                pipeline.id,
                stage.id,
                search,
                filters,
                ownerUserId,
                serviceTypeId,
                outcome,
              ])}
              pipeline={pipeline}
              stage={stage}
              search={search}
              filters={filters}
              ownerUserId={ownerUserId}
              serviceTypeId={serviceTypeId}
              outcome={outcome}
            />
          ))}
        </div>
      </div>
      {stages.length === 0 ? (
        <Notice tone="warn">
          This pipeline has no active stages. Configure its stages in Settings.
        </Notice>
      ) : null}
    </div>
  );
}

function PipelineColumn({
  pipeline,
  stage,
  search,
  filters,
  ownerUserId,
  serviceTypeId,
  outcome,
}: {
  pipeline: JobPipeline;
  stage: JobPipelineStage;
  search?: string;
  filters?: ListInput['filters'];
  ownerUserId?: string;
  serviceTypeId?: string;
  outcome?: JobStageOutcome;
}) {
  const trpc = useTRPC();
  const [offset, setOffset] = useState(0);
  const online = useOnline();
  const q = useQuery(
    trpc.jobPipeline.board.queryOptions({
      pipelineId: pipeline.id,
      stageId: stage.id,
      search,
      filters,
      ownerUserId,
      serviceTypeId,
      outcome,
      limit: PIPELINE_PAGE_SIZE,
      offset,
    }),
  );
  const state = shownState(q, q.data);
  const data = q.data?.stages.find((value) => value.id === stage.id);
  return (
    <section
      className="w-[min(300px,calc(100vw-48px))] shrink-0 rounded-[var(--radius)] border border-border bg-surface"
      aria-label={stage.label}
    >
      <header className="space-y-3 border-b border-border p-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold">
            <StatusBadge tone={stageTone(stage.color)}>{stage.label}</StatusBadge>
          </h2>
          <span className="text-xs tabular-nums text-muted-foreground">
            {data ? `${data.count} jobs` : '—'}
          </span>
        </div>
        {data ? <JobValueBuckets values={data} /> : null}
      </header>
      <div className="space-y-3 p-3">
        <StaleNotice
          show={refreshFailed(q, q.data) || q.isPaused}
          onRetry={() => void q.refetch()}
        />
        {state !== 'ready' ? (
          <UnresolvedState
            state={state}
            icon={IconJobs}
            what={`${stage.label} jobs`}
            onRetry={() => void q.refetch()}
            error={q.error}
          />
        ) : !data ? (
          <Notice tone="warn">
            This stage changed. Refresh the pipeline settings to continue.
          </Notice>
        ) : (
          <>
            {data.rows.length === 0 ? (
              <p className="py-5 text-center text-sm text-muted-foreground">
                {data.count > 0
                  ? 'No jobs on this page. Return to the previous page.'
                  : 'No jobs in this stage.'}
              </p>
            ) : (
              <ul className="space-y-3">
                {data.rows.map((job) => (
                  <li key={job.id}>
                    <PipelineJobCard
                      job={job}
                      pipeline={pipeline}
                      readOnly={!online || q.isPaused || q.isError || q.isFetching}
                    />
                  </li>
                ))}
              </ul>
            )}
            <div
              className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3"
              aria-label={`${stage.label} pages`}
            >
              <Button
                variant="ghost"
                size="sm"
                disabled={offset === 0 || q.isFetching}
                onClick={() => setOffset(Math.max(0, offset - PIPELINE_PAGE_SIZE))}
              >
                Previous
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {Math.floor(offset / PIPELINE_PAGE_SIZE) + 1}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={data.nextOffset === null || q.isFetching}
                onClick={() => {
                  if (data.nextOffset !== null) setOffset(data.nextOffset);
                }}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function PipelineJobCard({
  job,
  pipeline,
  readOnly,
}: {
  job: CommercialJobRow;
  pipeline: JobPipeline;
  readOnly: boolean;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const mayMove = can(useSession().role, 'commercialJob', 'update');
  const [stageId, setStageId] = useState(job.stageId);
  const move = useMutation(
    trpc.commercialJob.moveStage.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: trpc.jobPipeline.pathKey() });
        void qc.invalidateQueries({ queryKey: trpc.commercialJob.pathKey() });
      },
      onError: () => {
        void qc.invalidateQueries({ queryKey: trpc.jobPipeline.pathKey() });
      },
    }),
  );
  return (
    <article className="space-y-3 rounded-[var(--radius-sm)] border border-border bg-background p-3">
      <Link
        to="/jobs/$jobId"
        params={{ jobId: job.id }}
        className="block rounded-sm hover:underline"
      >
        <span className="font-mono text-xs text-muted-foreground">
          {formatCommercialJobNumber(job.jobNumber)}
        </span>
        <h3 className="mt-1 break-words text-sm font-semibold">{job.title}</h3>
      </Link>
      <p className="text-xs text-muted-foreground">
        {job.customerName ?? 'Customer not linked'}
        {job.ownerName ? ` · ${job.ownerName}` : ''}
      </p>
      <JobValueBuckets values={job} />
      {mayMove ? (
        <form
          className="space-y-2 border-t border-border pt-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!readOnly && !move.isPending && stageId !== job.stageId)
              move.mutate({
                commercialJobId: job.id,
                pipelineId: pipeline.id,
                stageId,
                expectedVersion: job.version,
                ...idem(),
              });
          }}
        >
          <label className="block text-xs text-muted-foreground">
            Move to stage
            <select
              className={`${field} mt-1`}
              aria-label={`Stage for ${formatCommercialJobNumber(job.jobNumber)}`}
              value={stageId}
              onChange={(event) => setStageId(event.target.value)}
              disabled={readOnly || move.isPending}
            >
              {pipeline.stages
                .filter((stage) => stage.active)
                .map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.label}
                  </option>
                ))}
            </select>
          </label>
          <Button
            type="submit"
            variant="ghost"
            size="sm"
            disabled={readOnly || move.isPending || stageId === job.stageId}
          >
            {move.isPending ? 'Moving…' : 'Move'}
          </Button>
          {move.error ? (
            <Notice tone="danger" alert>
              {move.error.message}
            </Notice>
          ) : null}
          {move.error && move.variables ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={move.isPending || readOnly}
              onClick={() => {
                if (move.variables) move.mutate(move.variables);
              }}
            >
              Retry this move
            </Button>
          ) : null}
        </form>
      ) : null}
    </article>
  );
}
